import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { Children, useEffect, useMemo, useRef } from 'react';
import ReactMarkdown, { type Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import type { Role } from '../types/assistant.types';
import { SourceCitation } from './source-citation';
import { useTypingReveal } from '../hooks/use-typing-reveal';
import { rehypeBidi, type RehypeBidiOptions } from '../helpers/rehype-bidi';
import {
  hasArabicLetters,
  replyDirection,
  splitLtrRuns,
  textDirection,
} from '../helpers/text-direction';

/** The visible text of a node tree, for deciding how to present it. */
function textOf(node: ReactNode): string {
  return Children.toArray(node)
    .map((child) =>
      typeof child === 'string' || typeof child === 'number'
        ? String(child)
        : typeof child === 'object' && child !== null && 'props' in child
          ? textOf((child.props as { children?: ReactNode }).children)
          : ''
    )
    .join('');
}

/**
 * Whether a link is the report-download call to action.
 *
 * The agent emits those as `[📄 Download … (PDF)](https://…?t=<token>)`, and they get
 * button styling so the friendly label shows instead of a URL carrying a token. Nothing
 * else should: an email address in a table cell, or a link inside a sentence, became a
 * full-size purple button that wrecked the row it sat in.
 */
function isDownloadCta(children: ReactNode): boolean {
  const text = textOf(children).trim();
  return text.startsWith('📄') || /\bdownloads?\b/i.test(text);
}

// Custom renderers keep the rendered Markdown compact and on-brand inside the chat
// bubble. rehype-sanitize strips any raw HTML (e.g. <script>) so assistant prose can
// never inject markup.
const markdownComponents = {
  // `node` is react-markdown's AST handle, not an HTML attribute — spreading it onto
  // the anchor rendered a literal node="[object Object]" into the DOM.
  a: ({
    href,
    children,
    node: _node,
    ...rest
  }: ComponentPropsWithoutRef<'a'> & { node?: unknown }) => {
    const citation = href?.match(
      /^\/api\/v1\/crm\/assistant\/knowledge-citations\/(\d+)\/$/
    );
    if (citation)
      return (
        <SourceCitation id={Number(citation[1])}>{children}</SourceCitation>
      );
    return (
      <a
        {...rest}
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        className={
          isDownloadCta(children)
            ? 'mt-1 inline-flex items-center gap-1.5 rounded-lg bg-primary-1000 px-3 py-1.5 text-xs font-medium text-white no-underline transition hover:opacity-90'
            : 'break-words text-primary-1000 underline underline-offset-2 hover:opacity-80'
        }
      >
        {children}
      </a>
    );
  },
  // `dir` comes from rehypeBidi; it is absent on a reply with no Arabic.
  p: ({ children, dir }: ComponentPropsWithoutRef<'p'>) => (
    <p
      dir={dir}
      className='my-2 first:mt-0 last:mb-0'
    >
      {children}
    </p>
  ),
  ul: ({ children, dir }: ComponentPropsWithoutRef<'ul'>) => (
    <ul
      dir={dir}
      className='my-2 list-disc ps-5'
    >
      {children}
    </ul>
  ),
  ol: ({ children, dir }: ComponentPropsWithoutRef<'ol'>) => (
    <ol
      dir={dir}
      className='my-2 list-decimal ps-5'
    >
      {children}
    </ol>
  ),
  li: ({ children, dir }: ComponentPropsWithoutRef<'li'>) => (
    <li
      dir={dir}
      className='my-1'
    >
      {children}
    </li>
  ),
  code: ({ children }: ComponentPropsWithoutRef<'code'>) => (
    <code className='rounded bg-black/5 px-1 py-0.5 font-mono text-[0.85em]'>
      {children}
    </code>
  ),
  pre: ({ children, dir }: ComponentPropsWithoutRef<'pre'>) => (
    <pre
      dir={dir}
      className='my-1 overflow-x-auto rounded-lg bg-black/5 p-2 text-xs'
    >
      {children}
    </pre>
  ),
  // The wrapper takes the table's direction too, so an Arabic table both runs
  // its columns right to left and starts scrolled at its first column.
  table: ({ children, dir }: ComponentPropsWithoutRef<'table'>) => (
    <div
      dir={dir}
      className='my-1 overflow-x-auto rounded-2xl border border-primary-200 bg-primary-50'
    >
      <table
        dir={dir}
        className='w-full border-collapse text-xs'
      >
        {children}
      </table>
    </div>
  ),
  th: ({ children, dir }: ComponentPropsWithoutRef<'th'>) => (
    <th
      dir={dir}
      className='border-b border-primary-200 px-3 py-2 text-start font-semibold text-gray-800'
    >
      {children}
    </th>
  ),
  td: ({ children, dir }: ComponentPropsWithoutRef<'td'>) => (
    <td
      dir={dir}
      className='border-b border-primary-100 px-3 py-2 text-gray-800 last:border-b-0'
    >
      {children}
    </td>
  ),
};

/**
 * rehypeBidi must run after rehype-sanitize: sanitize would otherwise strip the
 * `<bdi>` isolates it adds. Its direction inputs come from the FINAL content,
 * not the text typed so far.
 */
function rehypePlugins(
  content: string,
  revealing: boolean
): NonNullable<Options['rehypePlugins']> {
  return [
    rehypeSanitize,
    [
      rehypeBidi,
      {
        enabled: hasArabicLetters(content),
        reply: replyDirection(content),
        revealing,
      } satisfies RehypeBidiOptions,
    ],
  ];
}

/** The user's own text: plain, with English runs isolated when it reads RTL. */
function UserText({ content }: { content: string }) {
  if (textDirection(content) !== 'rtl') return content;
  return splitLtrRuns(content).map((segment, i) =>
    segment.ltr ? (
      <bdi
        key={i}
        dir='ltr'
      >
        {segment.text}
      </bdi>
    ) : (
      segment.text
    )
  );
}

export function MessageBubble({
  role,
  content,
  reveal = false,
  onGrow,
}: {
  role: Role;
  content: string;
  /** Type the text out once (a checked answer that arrived whole). */
  reveal?: boolean;
  /** Called each time the typed text grows (and once when it completes). */
  onGrow?: () => void;
}) {
  const isUser = role === 'user';
  const isError = !isUser && content.startsWith('⚠️');
  const typing = useTypingReveal(content, reveal && !isUser && !isError);
  const growRef = useRef(onGrow);
  growRef.current = onGrow;
  const typed = reveal ? typing.shown : null;
  useEffect(() => {
    if (typed !== null) growRef.current?.();
  }, [typed]);
  const plugins = useMemo(
    () => rehypePlugins(content, typing.revealing),
    [content, typing.revealing]
  );

  // Figma: user = primary-100 bubble, primary-1000 text, sharp bottom-right.
  // Assistant prose has no bubble — sits on the panel background.
  if (isUser) {
    return (
      <div className='flex justify-end'>
        <div
          data-slot='message-bubble'
          // Explicit, from the dominant script; absent when there is no Arabic.
          dir={hasArabicLetters(content) ? textDirection(content) : undefined}
          // The corners stay physical: the bubble's sharp corner points at the
          // side it sits on, which the page's direction decides, not the text's.
          className='max-w-[85%] whitespace-pre-wrap rounded-tl-3xl rounded-tr-3xl rounded-bl-3xl rounded-br-none bg-primary-100 px-3 py-2 text-base text-primary-1000'
        >
          <UserText content={content} />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex justify-start'>
        <div
          data-slot='message-bubble'
          className='max-w-[85%] rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700'
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={plugins}
            components={markdownComponents}
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>
    );
  }

  return (
    <div className='flex justify-start'>
      <div
        data-slot='message-bubble'
        data-revealing={typing.revealing ? '' : undefined}
        // Any press inside a typing answer completes it, so a link never moves
        // under the pointer between press and release.
        onPointerDown={typing.revealing ? typing.finish : undefined}
        className='max-w-full text-base text-gray-800'
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={plugins}
          components={markdownComponents}
        >
          {typing.shown}
        </ReactMarkdown>
      </div>
    </div>
  );
}
